import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CommerceSiteDestinationModal from './CommerceSiteDestinationModal';
import JustOnePageOverflowMenu from './JustOnePageOverflowMenu';
import { siteHasCommerceAdmin } from '../utils/commerce';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SiteRenderer from './SiteRenderer';
import SiteFontStyles from './SiteFontStyles';
import { migrateSite } from '../utils/defaults';
import PublicLinkActions from './PublicLinkActions';

const PREVIEW_WIDTH = 1200;
const PREVIEW_SCALE = 0.2;

function formatDate(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export function SitePreviewThumb({ site }) {
  const migrated = useMemo(() => migrateSite(site), [site]);
  const isEmpty = !(migrated.sections || []).length && !migrated.pageMenu;

  if (isEmpty) {
    return (
      <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-muted/80 via-muted/40 to-primary/5 flex flex-col items-center justify-center gap-2 border-b border-border/60">
        <Icon name="LayoutTemplate" size={32} className="text-muted-foreground/40" />
        <span className="text-xs text-muted-foreground">Page vierge</span>
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/10] bg-neutral-100 overflow-hidden border-b border-border/60">
      <SiteFontStyles typography={migrated.typography} />
      <div
        className="absolute left-1/2 top-0 pointer-events-none select-none"
        style={{
          width: PREVIEW_WIDTH,
          transform: `translateX(-50%) scale(${PREVIEW_SCALE})`,
          transformOrigin: 'top center',
        }}
      >
        <SiteRenderer
          sections={migrated.sections}
          pageMenu={migrated.pageMenu}
          pageBackground={migrated.pageBackground}
          typography={migrated.typography}
          viewport="desktop"
          interactive={false}
          playAnimations={false}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-card/30 via-transparent to-transparent pointer-events-none" />
    </div>
  );
}

export default function SiteLibraryCard({
  site,
  onDelete,
  onUnpublish,
  onPublish,
  busyId,
}) {
  const navigate = useNavigate();
  const [destOpen, setDestOpen] = useState(false);
  const isBusy = busyId === site._id;
  const commerceEnabled = siteHasCommerceAdmin(site);
  const sectionCount = site.sections?.length ?? 0;
  const componentCount =
    (site.sections || []).reduce((n, s) => n + (s.components?.length || 0), 0) +
    (site.pageMenu ? 1 : 0);
  const updatedLabel = formatDate(site.updatedAt);

  const previewInner = (
    <>
        <SitePreviewThumb site={site} />
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full backdrop-blur-md shadow-sm ${
              site.published
                ? 'bg-emerald-500/90 text-white'
                : 'bg-background/90 text-muted-foreground border border-border/80'
            }`}
          >
            {site.published ? 'En ligne' : 'Brouillon'}
          </span>
          {commerceEnabled ? (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <JustOnePageOverflowMenu
                buttonClassName="bg-background/95 border border-border shadow-sm"
                items={[
                  {
                    id: 'admin',
                    label: 'Administration',
                    icon: 'Store',
                    onClick: () =>
                      navigate(`/web-creator/edit/${site._id}/administration`),
                  },
                  {
                    id: 'editor',
                    label: 'Éditeur',
                    icon: 'Pencil',
                    onClick: () => navigate(`/web-creator/edit/${site._id}`),
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/95 text-sm font-medium shadow-lg">
            <Icon name={commerceEnabled ? 'LayoutGrid' : 'Pencil'} size={15} />
            {commerceEnabled ? 'Ouvrir le site' : "Ouvrir l'éditeur"}
          </span>
        </div>
    </>
  );

  return (
    <article className="group flex flex-col rounded-2xl border border-border/80 bg-card shadow-elevation-1 overflow-hidden transition-all duration-200 hover:shadow-elevation-2 hover:border-primary/25 hover:-translate-y-0.5">
      {commerceEnabled ? (
        <button
          type="button"
          onClick={() => setDestOpen(true)}
          className="block relative w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {previewInner}
        </button>
      ) : (
        <Link
          to={`/web-creator/edit/${site._id}`}
          className="block relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {previewInner}
        </Link>
      )}

      <CommerceSiteDestinationModal
        open={destOpen}
        siteId={site._id}
        siteTitle={site.title}
        onClose={() => setDestOpen(false)}
      />

      <div className="flex flex-col flex-1 p-4 gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-foreground truncate text-base leading-snug">
            {site.title || 'Sans titre'}
          </h2>
          {site.description ? (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{site.description}</p>
          ) : (
            <p className="text-xs text-muted-foreground/50 italic mt-1">Aucune description</p>
          )}
          <PublicLinkActions
            slug={site.slug}
            published={site.published}
            className="mt-2"
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Icon name="Layers" size={12} />
            {sectionCount} section{sectionCount !== 1 ? 's' : ''}
          </span>
          <span className="inline-flex items-center gap-1">
            <Icon name="LayoutGrid" size={12} />
            {componentCount} bloc{componentCount !== 1 ? 's' : ''}
          </span>
          {updatedLabel ? (
            <span className="inline-flex items-center gap-1 ml-auto">
              <Icon name="Clock" size={12} />
              {updatedLabel}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 pt-1 mt-auto border-t border-border/60">
          <Link to={`/web-creator/edit/${site._id}`} className="flex-1 min-w-[88px]">
            <Button size="sm" className="w-full" variant="default">
              <Icon name="Pencil" size={14} className="mr-1.5" />
              Éditer
            </Button>
          </Link>

          {site.published ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-amber-700 border-amber-200 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-900/50 dark:hover:bg-amber-950/30"
                disabled={isBusy}
                onClick={() => onUnpublish(site._id)}
              >
                {isBusy ? '…' : 'Dépublier'}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 min-w-[88px]"
              disabled={isBusy}
              onClick={() => onPublish(site._id)}
            >
              <Icon name="Globe" size={14} className="mr-1.5" />
              {isBusy ? '…' : 'Publier'}
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 px-2"
            title="Supprimer"
            onClick={() => onDelete(site._id)}
          >
            <Icon name="Trash2" size={15} />
          </Button>
        </div>
      </div>
    </article>
  );
}
